import Table from '@eduzz/houston-ui/Table';
import Typography from '@eduzz/houston-ui/Typography';
import { Tooltip } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import Icon from '@material-ui/core/Icon';
import Alert from 'components/Alert';
import Truncate from 'components/Truncate';
import moment from 'moment';
import React, { memo, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import TableOptions from 'v2/components/Shared/TableOptions';
import affiliateService from 'v2/services/affiliate';

import type { IAffiliate } from 'v2/interfaces/affiliate';

import CustomCommissionForm from './CustomCommissionForm';
import ModalNewMessage from './ModalNewMessage';
import { useStyles } from './styles';

interface IProps {
  onSort: (sortField: string, sortDirection: 'asc' | 'desc') => void;
}

const TableApproved = ({ onSort }: IProps) => {
  const classes = useStyles();

  const history = useHistory();

  const [isLoading] = useObservable(() => affiliateService.isLoading(), []);
  const [affiliates] = useObservable(() => affiliateService.getAffiliates(), []);
  const [pagination] = useObservable(() => affiliateService.getPaginationResponse(), []);
  const [currentSort] = useObservable(() => affiliateService.getCurrentSort(), []);

  const [affiliateSelected, setAffiliateSelected] = useState<IAffiliate | null>(null);
  const [affiliatesSelected, setAffiliatesSelected] = useState<Array<IAffiliate>>([]);
  const [modalNewMessage, setModalNewMessage] = useState(false);
  const [openModalCommision, setOpenModalCommision] = useState(false);

  const toggleModalCommission = (affiliate: IAffiliate) => {
    setOpenModalCommision(!openModalCommision);
    setAffiliateSelected(affiliate);
  };

  const toggleModalNewMessage = () => {
    setModalNewMessage(!modalNewMessage);
  };

  const onChangeRowsPerPage = (per_page: number) => {
    affiliateService.setPagination({ ...pagination, ...{ per_page, current_page: 1 } });
    document.getElementById('affiliates_table').scrollIntoView();
  };

  const onChangePage = (page: number) => {
    affiliateService.setPagination({ ...pagination, ...{ current_page: page } });
    document.getElementById('affiliates_table').scrollIntoView();
  };

  const resetAffiliatesSelected = () => {
    setAffiliatesSelected([]);
  };

  const selectAllAffiliates = () => {
    const selected = affiliates.map((a) => a);

    if (affiliates.length > affiliatesSelected.length) {
      setAffiliatesSelected(selected);
      return;
    }

    setAffiliatesSelected([]);
  };

  const toggleSelect = (affiliate: IAffiliate) => {
    let selected: Array<IAffiliate> = [];

    if (affiliatesSelected.filter((a) => a.id === affiliate.id).length) {
      selected = affiliatesSelected.filter((a) => a.id !== affiliate.id);
      setAffiliatesSelected(selected);
      return;
    }

    selected = [...affiliatesSelected, affiliate];
    setAffiliatesSelected(selected);
  };

  const handleClickModerate = (id: number, status: 'A' | 'R') => {
    affiliateService.moderateAffiliate(id, status).subscribe(() => {
      setAffiliatesSelected([]);
      toast.success('Afiliação cancelada com sucesso.');
    }, toast.error);
  };

  const repprovedAffiliates = async () => {
    const confirmation = await Alert.show({
      title: 'Atenção',
      message: 'Deseja realmente cancelar a afiliação dos afiliados selecionados?',
      confirmation: true,
      buttonOk: 'Cancelar afiliação',
      buttonCancel: 'Cancelar',
    });

    if (!confirmation) {
      return;
    }

    const affs = affiliatesSelected.map((a) => a.id);

    affiliateService.moderateMultipleAffiliates(affs, 'R').subscribe(() => {
      setAffiliatesSelected([]);
      toast.success('Afiliados reprovados com sucesso.');
    }, toast.error);
  };

  const checkIfAffiliateIsSelect = (affiliateId: number) => !!affiliatesSelected.filter((a) => a.id === affiliateId).length;

  const goToMessagesPage = (affiliate: IAffiliate) => {
    history.push(`/producer/messages?affiliate=${affiliate.affiliate_id}`);
  };

  const tableOptionsPerPage = '10,20,50,100,200';
  return (
    <>
      {!!affiliatesSelected.length && (
        <div className={classes.tableOptionsContainer}>
          <TableOptions
            items={affiliatesSelected.length}
            style={{
              top: 0,
              padding: 16,
            }}
            options={[
              {
                label: 'Cancelar',
                callback: () => resetAffiliatesSelected(),
              },
              {
                icon: 'thumb_down',
                label: 'Cancelar Afiliação',
                callback: () => repprovedAffiliates(),
              },
              {
                icon: 'message',
                label: 'Enviar Mensagem',
                color: 'primary',
                callback: () => toggleModalNewMessage(),
              },
            ]}
          />
        </div>
      )}

      <Table id='affiliates_table' loading={isLoading} total={pagination?.total_items || 0}>
        <Table.Header sortDirection={currentSort?.direction} sortField={currentSort?.field} onSort={onSort}>
          <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
            <Table.Column width={45}>
              <Checkbox
                indeterminate={!!affiliatesSelected?.length && affiliatesSelected?.length < affiliates?.length}
                checked={!!affiliates?.length && affiliatesSelected?.length === affiliates?.length}
                onChange={() => selectAllAffiliates()}
                disabled={!affiliates?.length}
              />
            </Table.Column>
          </PermissionHide>
          <Table.Column width={170} sortableField='request_date'>
            Data da solicitação
          </Table.Column>
          <Table.Column width={250} sortableField='product_title'>
            Produto
          </Table.Column>
          <Table.Column sortableField='affiliate_name'>Afiliado</Table.Column>
          <Table.Column width={50} sortableField='funnel_leads'>
            F.I
          </Table.Column>
          <Table.Column width={90} sortableField='sales_quantity'>
            Vendas
          </Table.Column>
          <Table.Column width={130} sortableField='revenues'>
            Faturamento
          </Table.Column>
          <Table.Column width={190} sortableField='custom_commission'>
            Com. diferenciada
          </Table.Column>
        </Table.Header>

        <Table.Body>
          {affiliates?.map((affiliate, index) => (
            <Table.Row data={affiliate} key={affiliate.id} index={index}>
              <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
                <Table.Cell>
                  <Checkbox checked={checkIfAffiliateIsSelect(affiliate.id)} onClick={() => toggleSelect(affiliate)} />
                </Table.Cell>
              </PermissionHide>
              <Table.Cell>
                <Typography>{moment(affiliate.request_date).format('L')}</Typography>
              </Table.Cell>
              <Table.Cell>
                <Typography size='xxs' weight='semibold'>
                  <Truncate count={30} final='...'>
                    {affiliate.product_title}
                  </Truncate>
                </Typography>
                <Typography size='xxs'>({affiliate.product_id})</Typography>
              </Table.Cell>
              <Table.Cell>
                <Typography>
                  <a href={`/producer/detail/${affiliate.affiliate_id}`} target='_blank' rel='noopener noreferrer'>
                    <Truncate count={25} final={'...'}>
                      {affiliate.affiliate_name}
                    </Truncate>
                  </a>
                </Typography>
              </Table.Cell>
              <Table.Cell>
                <Typography>{affiliate.funnel_leads}</Typography>
              </Table.Cell>
              <Table.Cell>
                <Tooltip title='Em breve voltaremos a mostrar esses valores'>
                  <div className={classes.unavailable}>
                    Indisponível <Icon>help</Icon>
                  </div>
                </Tooltip>
              </Table.Cell>
              <Table.Cell align='right'>
                <Tooltip title='Em breve voltaremos a mostrar esses valores'>
                  <div className={classes.unavailable}>
                    Indisponível <Icon>help</Icon>
                  </div>
                </Tooltip>
              </Table.Cell>
              <Table.Cell>
                <Typography>
                  {affiliate.custom_commission === 0 && affiliate.custom_commission_type === null ? (
                    'Não configurado'
                  ) : (
                    <>
                      {affiliate.custom_commission_type === 'V' && <FormattedNumber value={affiliate.custom_commission} format='BRL' />}
                      {affiliate.custom_commission_type === 'P' && (
                        <>
                          <FormattedNumber value={affiliate.custom_commission} />%
                        </>
                      )}
                    </>
                  )}
                </Typography>
              </Table.Cell>
              <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
                <>
                  <Table.Action onClick={toggleModalCommission}>Comissão diferenciada</Table.Action>
                  <Table.Action onClick={goToMessagesPage}>Enviar mensagem</Table.Action>
                  <Table.Action onClick={() => handleClickModerate(affiliate.id, 'R')}>Cancelar afiliação</Table.Action>
                </>
              </PermissionHide>
            </Table.Row>
          ))}
        </Table.Body>

        {!isLoading && pagination && (
          <Table.Pagination
            page={pagination?.current_page}
            perPage={pagination?.per_page}
            optionsPerPage={tableOptionsPerPage}
            onChangePage={onChangePage}
            onChangePerPage={onChangeRowsPerPage}
          />
        )}
      </Table>

      <ModalNewMessage active={modalNewMessage} closeModal={toggleModalNewMessage} affiliatesSelected={affiliatesSelected} />

      <CustomCommissionForm open={openModalCommision} handleClose={toggleModalCommission} affiliate={affiliateSelected} />
    </>
  );
};

export default memo(TableApproved);
